import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export interface BreadcrumbItem {
  label: string;
  translationKey: string;
  url: string;
  isActive: boolean;
  icon?: string;
  dynamicLabel?: string; // Para substituir o label padrão com informações dinâmicas
}

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private breadcrumbsSubject = new BehaviorSubject<BreadcrumbItem[]>([]);
  public breadcrumbs$ = this.breadcrumbsSubject.asObservable();

  // Mapeamento de rotas para chaves de tradução e ícones
  private routeConfig: { [key: string]: { translationKey: string; icon?: string } } = {
    'home': { translationKey: 'breadcrumb.home', icon: 'home' },
    'favorite': { translationKey: 'home.favorites', icon: 'favorite' },
    'dashboard': { translationKey: 'breadcrumb.dashboard', icon: 'dashboard' },
    'relatorio2': { translationKey: 'breadcrumb.reports', icon: 'assessment' },
    'webapi': { translationKey: 'breadcrumb.webapi', icon: 'api' },
    'user_preferences': { translationKey: 'breadcrumb.profile', icon: 'person' },
    'conexao': { translationKey: 'dmdi.connection.title', icon: 'link' },
    'projeto': { translationKey: 'dmdi.project.title', icon: 'folder' },
    'catalogodados': { translationKey: 'dmdi.columns.name', icon: 'storage' },
    'qualidadedados': { translationKey: 'dmdi.stages.quality.title', icon: 'verified' },
    'instancia': { translationKey: 'dmdi.columns.name', icon: 'computer' },
    'acao': { translationKey: 'dmdi.actions.title', icon: 'play_arrow' },
    'agendamento': { translationKey: 'dmdi.columns.name', icon: 'schedule' },
    'cargatrabalho': { translationKey: 'dmdi.columns.name', icon: 'work' },
    'workspace': { translationKey: 'dmdi.workspace.title', icon: 'workspaces' },
    'project': { translationKey: 'dmdi.project.title', icon: 'folder' },
    'connection': { translationKey: 'dmdi.connection.title', icon: 'link' },
    'action': { translationKey: 'dmdi.actions.title', icon: 'play_arrow' },
    'data-lineage': { translationKey: 'breadcrumb.dataLineage', icon: 'account_tree' }
  };

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService
  ) {
    this.initializeBreadcrumbs();
  }

  private initializeBreadcrumbs(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateBreadcrumbs();
      });
    
    // Update breadcrumbs on initial load
    this.updateBreadcrumbs();
  }

  private updateBreadcrumbs(): void {
    const breadcrumbs = this.buildBreadcrumbs();
    this.breadcrumbsSubject.next(breadcrumbs);
  }

  private buildBreadcrumbs(): BreadcrumbItem[] {
    const breadcrumbs: BreadcrumbItem[] = [];
    
    // Always start with Home
    const homeConfig = this.routeConfig['home'];
    breadcrumbs.push({
      label: 'Home',
      translationKey: homeConfig.translationKey,
      url: '/home',
      isActive: false,
      icon: homeConfig.icon
    });

    // Get current route segments
    const url = this.router.url;
    const segments = url.split('/').filter(segment => segment !== '');
    
    // Remove 'home' from segments if it exists since we already added it
    const filteredSegments = segments.filter(segment => segment !== 'home');
    
    if (filteredSegments.length > 0) {
      // Build breadcrumb path
      let currentPath = '';
      
      filteredSegments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        const isLast = index === filteredSegments.length - 1;
        const config = this.routeConfig[segment];
        
        breadcrumbs.push({
          label: config?.translationKey || this.capitalizeFirst(segment),
          translationKey: config?.translationKey || this.capitalizeFirst(segment),
          url: currentPath,
          isActive: isLast,
          icon: config?.icon
        });
      });
    } else {
      // If we're on home page, mark it as active
      breadcrumbs[0].isActive = true;
    }

    return breadcrumbs;
  }

  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).replace(/-/g, ' ');
  }

  // Método para adicionar breadcrumb customizado (para casos especiais)
  addCustomBreadcrumb(item: BreadcrumbItem): void {
    const currentBreadcrumbs = this.breadcrumbsSubject.value;
    // Se não tiver translationKey, usar o label como fallback
    if (!item.translationKey) {
      item.translationKey = item.label;
    }
    const updatedBreadcrumbs = [...currentBreadcrumbs, item];
    this.breadcrumbsSubject.next(updatedBreadcrumbs);
  }

  // Método para limpar breadcrumbs customizados
  clearCustomBreadcrumbs(): void {
    this.updateBreadcrumbs();
  }

  // Método para obter breadcrumbs atuais
  getCurrentBreadcrumbs(): BreadcrumbItem[] {
    return this.breadcrumbsSubject.value;
  }

  // Método para atualizar breadcrumb específico com label dinâmico
  updateBreadcrumbLabel(routeSegment: string, dynamicLabel: string): void {
    const currentBreadcrumbs = this.getCurrentBreadcrumbs();
    const updatedBreadcrumbs = currentBreadcrumbs.map(breadcrumb => {
      if (breadcrumb.url.includes(routeSegment)) {
        return { ...breadcrumb, dynamicLabel };
      }
      return breadcrumb;
    });
    this.breadcrumbsSubject.next(updatedBreadcrumbs);
  }
}
